import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useDataBus } from "@/lib/dataBus";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, ClipboardList, FolderOpen, Users, TrendingUp, Clock, FileEdit, Send, Eye,
  Sparkles, Settings2, Check, Megaphone,
} from "lucide-react";
import { Link } from "react-router-dom";
import { NewChangesBanner, AnnouncementsBanner } from "@/components/projects/ProjectAlerts";
import DayCardsManager from "@/components/DayCardsManager";
import AnnouncementDialog from "@/components/AnnouncementDialog";
import GoogleUpdateBanner from "@/components/GoogleUpdateBanner";

const STATUS_META = {
  "To-Do": { icon: ClipboardList, cls: "bg-slate-100 text-slate-700 border-slate-200" },
  "Editing Pending": { icon: FileEdit, cls: "bg-amber-100 text-amber-700 border-amber-200" },
  "Publishing Pending": { icon: Send, cls: "bg-orange-100 text-orange-700 border-orange-200" },
  "Review Pending": { icon: Eye, cls: "bg-blue-100 text-blue-700 border-blue-200" },
  "Published": { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  // Day Card — random per user, per day. Fetched from /day-cards/mine which
  // returns a stable pick for today plus a default fallback when the pool
  // is empty. Manager dialog is only wired for admin roles.
  const [dayCard, setDayCard] = useState(null);
  // Iter 44 / 44c.2 — two separate banners on the home dashboard:
  //   • projectNotifs → "New Changes" banner (project mutations feed)
  //   • announcements  → "Announcements" banner (custom broadcast feed)
  // They're fetched, dismissed, and rendered independently so admins can
  // clear announcements without also clearing project activity, and vice
  // versa.
  const [projectNotifs, setProjectNotifs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showDayCardsMgr, setShowDayCardsMgr] = useState(false);
  const [showAnnouncementDlg, setShowAnnouncementDlg] = useState(false);
  const canManageCards = user && ["super_admin", "admin", "department_admin"].includes(user.role);
  // Mirrors backend _can_post_announcement: admin-tier OR department_admin
  // with HR or Content in their DA departments (multi-position aware).
  const canPostAnnouncement = (() => {
    if (!user) return false;
    const primaryRole = user.role;
    const positions = user.positions || [];
    if (["super_admin", "admin"].includes(primaryRole)) return true;
    if (positions.some((p) => ["super_admin", "admin"].includes(p?.role))) return true;
    const isPostDept = (d) => ["hr", "content"].includes((d || "").toLowerCase());
    const primaryDepts = user.departments || (user.department ? [user.department] : []);
    if (primaryRole === "department_admin" && primaryDepts.some(isPostDept)) return true;
    if (positions.some((p) => p?.role === "department_admin" && isPostDept(p?.department))) return true;
    return false;
  })();

  useEffect(() => {
    (async () => {
      // Iter 42.6 — replaced `api.get("/tasks")` with the new bounded
      // `/dashboard/recent-tasks?limit=8` endpoint. Old code downloaded
      // every visible task (up to 5000) just to render 8 rows, which was a
      // ~30-100 MB payload for accounts with 100k tasks and routinely froze
      // the browser on first paint.
      const [s, t, p, u, dc, notifs, customs] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/recent-tasks?limit=8"),
        api.get("/projects"),
        api.get("/users"),
        api.get("/day-cards/mine").catch(() => ({ data: null })),
        api.get("/project-notifications").catch(() => ({ data: [] })),
        api.get("/custom-notifications").catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setRecent(t.data || []);
      setProjects(p.data);
      setUsers(u.data);
      setDayCard(dc.data);
      setProjectNotifs(notifs.data || []);
      setAnnouncements(customs.data || []);
    })();
  }, []);

  // Iter 44 / 44c.2 — independent Ignore + bulk-Ignore per banner.
  // Optimistic UI + silent recovery on backend error.
  const ignoreProjectNotif = async (n) => {
    setProjectNotifs((prev) => prev.filter((x) => x.id !== n.id));
    try { await api.post(`/project-notifications/${n.id}/ack`); } catch { /* silent */ }
  };
  const ignoreAllProjectNotifs = async () => {
    setProjectNotifs([]);
    try { await api.post("/project-notifications/ack-all"); } catch { /* silent */ }
  };
  const ignoreAnnouncement = async (n) => {
    setAnnouncements((prev) => prev.filter((x) => x.id !== n.id));
    try { await api.post(`/custom-notifications/${n.id}/ack`); } catch { /* silent */ }
  };
  const ignoreAllAnnouncements = async () => {
    setAnnouncements([]);
    try { await api.post("/custom-notifications/ack-all"); } catch { /* silent */ }
  };
  const onAnnouncementCreated = () => {
    // Creator is auto-acked server-side, so nothing to prepend for them.
    setShowAnnouncementDlg(false);
  };

  // Iter 36.8 — Cross-user live sync. Refresh projects + users when any local
  // mutation lands (via dataBus) AND poll every 20 s / on window focus so
  // changes made by other users show up here too. Day Card and stats are only
  // fetched once — they don't need live sync.
  //
  // Iter 45.3 — the two banner feeds (`/project-notifications` and
  // `/custom-notifications`) are pulled in on the same tick so a new
  // announcement / project mutation appears within ~20 s without a hard
  // refresh. Backend already excludes items the caller has acked, so an
  // "Ignore" here won't reappear on the next tick.
  const refetchLive = async () => {
    try {
      const [p, u, notifs, customs] = await Promise.all([
        api.get("/projects"),
        api.get("/users"),
        api.get("/project-notifications").catch(() => ({ data: [] })),
        api.get("/custom-notifications").catch(() => ({ data: [] })),
      ]);
      setProjects(p.data || []);
      setUsers(u.data || []);
      setProjectNotifs(notifs.data || []);
      setAnnouncements(customs.data || []);
    } catch (e) { /* silent */ }
  };
  useDataBus(refetchLive, ["users", "projects", "any"]);
  useEffect(() => {
    const iv = setInterval(refetchLive, 20000);
    const onFocus = () => refetchLive();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const projectMap = Object.fromEntries(projects.map((p) => [p.id, p]));

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="dashboard-page">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500 mt-0.5">Welcome back, {user?.name}. Here's what's happening.</p>
          </div>
          <div className="flex items-center gap-3">
            {canPostAnnouncement && (
              <Button
                onClick={() => setShowAnnouncementDlg(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white h-9"
                data-testid="new-announcement-btn"
              >
                <Megaphone className="h-4 w-4 mr-1.5" />
                New Announcement
              </Button>
            )}
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 capitalize">
              {user?.role?.replace("_", " ")}
            </Badge>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-8">
        {/* Iter 45.68 — Google algorithm rollout banner on the main dashboard
            so nobody misses it (shared component, also on SEO Performance). */}
        <GoogleUpdateBanner />
        {/* Iter 44c.2 — Announcements banner (broadcast messages posted by
            Founder/Admin/Content Admin/HR) is rendered FIRST so it always
            grabs attention before per-project activity. */}
        <AnnouncementsBanner
          announcements={announcements}
          onIgnore={ignoreAnnouncement}
          onIgnoreAll={ignoreAllAnnouncements}
        />

        {/* Iter 44 — "New Changes" banner: project mutations only. Backend
            /project-notifications is RBAC-gated (Admin/Super-Admin/Content
            DA); non-eligible viewers get an empty list and no banner. */}
        <NewChangesBanner
          notifications={projectNotifs}
          onIgnore={ignoreProjectNotif}
          onIgnoreAll={ignoreAllProjectNotifs}
        />

        {/* Day Card — big, quote-styled panel at the top of the dashboard.
            Fetched from /day-cards/mine (random per user per day). Admins get
            a gear icon to open the DayCardsManager dialog. Non-admins with an
            empty pool see the platform default message. */}
        {dayCard && (
          <div
            className="relative rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-rose-50 overflow-hidden"
            data-testid="dashboard-day-card"
          >
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-amber-100 rounded-full opacity-30 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-rose-100 rounded-full opacity-30 blur-3xl pointer-events-none" />
            <div className="relative px-8 py-8 sm:py-10 flex items-start gap-6">
              <div className="hidden sm:flex flex-shrink-0 w-14 h-14 rounded-full bg-white shadow-sm items-center justify-center border border-amber-200">
                <Sparkles className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.28em] text-amber-600 font-semibold mb-2">Day Card</div>
                <blockquote
                  className="font-display text-slate-900 leading-[1.15] tracking-tight"
                  style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.6rem)" }}
                  data-testid="dashboard-day-card-msg"
                >
                  <span className="text-amber-400/70 font-serif align-top mr-1 text-[1.5em] leading-none">“</span>
                  {dayCard.message}
                  <span className="text-amber-400/70 font-serif align-bottom ml-1 text-[1.5em] leading-none">”</span>
                </blockquote>
                {dayCard.is_default && (
                  <p className="text-xs text-slate-400 mt-3 italic">Default message · admins can upload custom cards for {user?.departments?.[0] || user?.department || "your department"}.</p>
                )}
              </div>
              {canManageCards && (
                <button
                  onClick={() => setShowDayCardsMgr(true)}
                  className="text-slate-400 hover:text-slate-700 transition"
                  aria-label="Manage day cards"
                  data-testid="dashboard-day-card-manage"
                >
                  <Settings2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={ClipboardList} label="Total Tasks" value={stats?.total_tasks ?? "—"} color="indigo" testId="kpi-tasks" />
          <KpiCard icon={FolderOpen} label="Projects" value={stats?.total_projects ?? "—"} color="blue" testId="kpi-projects" />
          <KpiCard icon={Users} label="Team Members" value={stats?.total_users ?? "—"} color="emerald" testId="kpi-users" />
          <KpiCard icon={TrendingUp} label="Published" value={stats?.by_status?.["Published"] ?? "—"} color="green" testId="kpi-published" />
        </div>

        {/* Status Breakdown */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Task Status Breakdown</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(STATUS_META).map(([status, meta]) => {
              const Icon = meta.icon;
              const count = stats?.by_status?.[status] ?? 0;
              return (
                <div key={status} className={`p-4 rounded-lg border ${meta.cls}`} data-testid={`status-count-${status}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">{status}</span>
                  </div>
                  <div className="font-display text-2xl font-bold">{count}</div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Recent tasks + projects */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 bg-white border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Recent Tasks</h3>
              <Link to="/my-tasks" className="text-sm text-purple-600 hover:underline">View all →</Link>
            </div>
            <div className="space-y-2">
              {recent.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No tasks yet</div>}
              {recent.map((t) => {
                const meta = STATUS_META[t.status] || STATUS_META["To-Do"];
                return (
                  <Link
                    key={t.id}
                    to={`/projects/${t.project_id}`}
                    data-testid={`recent-task-${t.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200"
                  >
                    <div className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${meta.cls}`}>
                      {t.status}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{t.keyword || "(empty keyword)"}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {projectMap[t.project_id]?.name || "—"}
                        {t.writer_id && ` · Writer: ${userMap[t.writer_id]?.name || "—"}`}
                      </div>
                    </div>
                    <Clock className="h-3.5 w-3.5 text-slate-400" />
                  </Link>
                );
              })}
            </div>
          </Card>

          <Card className="p-6 bg-white border-slate-200">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Projects</h3>
            <div className="space-y-2">
              {projects.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No projects</div>}
              {projects.slice(0, 6).map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="h-8 w-8 rounded bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate group-hover:text-purple-700">{p.name}</div>
                    <div className="text-xs text-slate-400 truncate">{p.site_url}</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <DayCardsManager
        open={showDayCardsMgr}
        onClose={() => setShowDayCardsMgr(false)}
        currentUser={user}
        initialDepartment={dayCard?.department || (user?.departments?.[0] || user?.department)}
      />
      <AnnouncementDialog
        open={showAnnouncementDlg}
        onClose={() => setShowAnnouncementDlg(false)}
        onCreated={onAnnouncementCreated}
      />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, testId }) {
  const colorMap = {
    indigo: "bg-purple-50 text-purple-600 border-purple-100",
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    green: "bg-green-50 text-green-600 border-green-100",
  };
  return (
    <Card className="p-5 bg-white border-slate-200" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center border ${colorMap[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}
